import React from "react";
import { motion } from "framer-motion";
import {
  Banknote,
  Handshake,
  Scale,
  LineChart,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const pillars = [
  {
    slug: "funding",
    icon: Banknote,
    overline: "01 · Funding Solutions",
    title: "Capital, structured for growth",
    body: "Company-specific debt, equity and hybrid structures — calibrated to your stage, sector and runway.",
    groups: [
      {
        heading: "Debt Funding Solutions",
        items: [
          {
            name: "Loan Syndication & Corporate Term Loans",
            desc: "Multi-bank term loans structured to your business profile — competitive rates and repayment terms aligned with long-term corporate growth.",
          },
          {
            name: "Project & Structured Finance",
            desc: "Bespoke financing for large infrastructure and capital-intensive projects, blending risk allocation with instruments that make ambitious ventures bankable.",
          },
          {
            name: "Working Capital & Trade Finance",
            desc: "Customised credit lines, invoice discounting and trade finance to optimise your cash conversion cycle so growth is never starved of liquidity.",
          },
          {
            name: "Debt Restructuring & Refinance",
            desc: "Renegotiate existing obligations to lower borrowing cost and realign repayment schedules with your current and projected cash flow reality.",
          },
        ],
      },
      {
        heading: "Equity-Based Funding",
        items: [
          {
            name: "Private Equity",
            desc: "We connect growth-stage companies with PE funds that bring capital, governance frameworks and networks to scale meaningfully.",
          },
          {
            name: "Venture Capital",
            desc: "VC funding for high-growth businesses — matching visionary founders with investors who understand risk, disruption and startup dynamics.",
          },
          {
            name: "Angel Investing",
            desc: "Curated introductions to angel investors providing seed capital, industry knowledge and mentorship at the earliest stage.",
          },
          {
            name: "SME IPOs",
            desc: "End-to-end BSE/NSE SME IPO execution — documentation, SEBI compliance, roadshows and listing to move from private to publicly traded.",
          },
        ],
      },
      {
        heading: "Hybrid Funding",
        items: [
          {
            name: "Mezzanine Financing",
            desc: "Blends debt and equity features to fund growth without immediate dilution — flexible capital that protects promoter ownership and control.",
          },
          {
            name: "Convertible Bonds",
            desc: "Structures offering the security of debt with equity upside on conversion — a balanced instrument for investor protection and company flexibility.",
          },
        ],
      },
    ],
  },
  {
    slug: "ma",
    icon: Handshake,
    overline: "02 · Mergers, Acquisitions & Deal Advisory",
    title: "Buy-side, sell-side, end-to-end",
    body: "Mandate-led M&A, exits and strategic transactions with airtight diligence and negotiation rigour.",
    items: [
      {
        name: "Mergers & Acquisitions",
        desc: "Advise acquirers and targets across the full lifecycle — identification, diligence, valuation, term sheet negotiation and post-merger integration.",
      },
      {
        name: "Sale of Businesses",
        desc: "Position the business compellingly to strategic and financial buyers and manage the entire sale to maximise realisation and ensure a seamless transition.",
      },
      {
        name: "Transaction Advisory & Bid Processing",
        desc: "End-to-end transaction management — bid structuring, information memoranda, data rooms and stakeholder coordination for competitive, timely closures.",
      },
      {
        name: "Financial Analysis & Planning",
        desc: "Robust financial models, scenario analyses and strategic plans that give promoters and boards clarity to make informed, well-defended decisions.",
      },
    ],
  },
  {
    slug: "valuation",
    icon: Scale,
    overline: "03 · Valuations & TEV",
    title: "Defensible numbers, board-ready",
    body: "Independent valuations grounded in cash flows, comparables and category nuance — built to withstand scrutiny.",
    items: [
      {
        name: "Company Valuation",
        desc: "Comprehensive business valuations using DCF, comparable company analysis and asset-based methods — designed to hold up before investors, lenders and regulators.",
      },
      {
        name: "Brand Valuation",
        desc: "Quantify brand equity through royalty relief, price premium and income-based methods — a lever in licensing, M&A, fundraises and balance-sheet strengthening.",
      },
      {
        name: "TEV — Techno-Economic Viability",
        desc: "Bank-grade TEV studies covering technical feasibility, market potential and financial projections — RBI/DFI-aligned rigour that accelerates project finance sanctions.",
      },
    ],
  },
  {
    slug: "advisory",
    icon: LineChart,
    overline: "04 · Advisory & Research",
    title: "Senior counsel on every page",
    body: "Financial business advisory and business research that translates ambition into a credible plan.",
    items: [
      {
        name: "Local Market Insight",
        desc: "Deep understanding of regional economic drivers, regulatory landscape and investment opportunities specific to Andhra Pradesh and coastal India.",
      },
      {
        name: "Robust Regional Network",
        desc: "Extensive connections with local banks, financial institutions, investors and key industry players — access that only a senior local practice can offer.",
      },
      {
        name: "Dedicated Local Support",
        desc: "Personalised advisory from a Visakhapatnam-based team — prompt, accessible and fully engaged with your business through every stage of a mandate.",
      },
    ],
  },
];

const card = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.6 },
  }),
};

const SubItem = ({ name, desc }) => (
  <li className="border-t border-white/5 pt-3">
    <p className="text-sm font-semibold text-white leading-snug">
      <span className="mr-2 inline-block w-1 h-1 rounded-full bg-brand-accent align-middle" />
      {name}
    </p>
    <p className="mt-1.5 text-[12.5px] text-white/60 leading-relaxed">{desc}</p>
  </li>
);

export const ServicePillars = () => {
  return (
    <section id="services" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-overline text-brand-accent">What we do</p>
            <h2 className="mt-4 font-serif text-white text-4xl md:text-5xl tracking-tight leading-tight">
              Four pillars. One operating philosophy:{" "}
              <span className="text-brand-accent italic">precision capital</span>.
            </h2>
          </div>
          <p className="md:max-w-sm text-sm md:text-base text-white/60 leading-relaxed">
            Every mandate is led by a senior banker. No junior hand-offs, no templated
            decks — only the analysis your board deserves.
          </p>
        </div>

        <div
          id="valuation"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.slug}
                id={`pillar-${p.slug}`}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={card}
                className="hover-lift relative bg-brand-surface border border-brand-border rounded-md p-6 md:p-8 flex flex-col h-full scroll-mt-28"
                data-testid={SERVICES.pillar(p.slug)}
              >
                <div className="absolute top-0 left-0 right-0 h-px bg-white/5" />
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-md bg-brand-primary/15 border border-brand-primary/30 flex items-center justify-center text-brand-accent">
                    <Icon className="w-5 h-5" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/30" />
                </div>

                <p className="mt-6 text-overline text-white/50">{p.overline}</p>
                <h3 className="mt-2 font-serif text-white text-2xl md:text-3xl leading-tight tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm md:text-base text-white/70 leading-relaxed">
                  {p.body}
                </p>

                {/* Grouped structure for Funding, flat for others */}
                {p.groups ? (
                  <div className="mt-6 space-y-6">
                    {p.groups.map((g) => (
                      <div key={g.heading}>
                        <p className="text-[11px] tracking-[0.22em] uppercase text-brand-accent font-semibold">
                          {g.heading}
                        </p>
                        <ul className="mt-3 space-y-3">
                          {g.items.map((it) => (
                            <SubItem key={it.name} {...it} />
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="mt-6 space-y-3">
                    {p.items.map((it) => (
                      <SubItem key={it.name} {...it} />
                    ))}
                  </ul>
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicePillars;
